import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import {
  Button,
  Checkbox,
  HeadingSmall,
  Input,
  LabelMedium,
  ListLoadingBox,
  Modal,
  ParagraphSmall,
  ProgressBarCircular,
  SearchIcon,
  Skeleton,
  Tokens,
  useStyletron,
} from '@visualfabriq/vf-ui-kit';
import { atom, useAtom } from 'jotai';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { InstancesApi, PipelinesApi } from 'src/api-new/bifrost';
import { Show } from 'src/components';
import { List } from 'src/components/list';
import { useBifrostApi } from 'src/services/useBifrostApi';
import { downloadBlobFile } from 'src/utils/files/downloadBlobFile';

type PipelineSelectionAction =
  | { type: 'toggle_all'; pipelineIds: string[] }
  | { type: 'toggle_one'; pipelineId: string }
  | { type: 'reset' };

const baseSelectionAtom = atom(new Set<string>());

const pipelineSelectionAtom = atom(
  (get) => get(baseSelectionAtom),
  (get, set, action: PipelineSelectionAction) => {
    const state = get(baseSelectionAtom);
    const next = new Set(state);

    switch (action.type) {
      case 'toggle_all': {
        const allSelected = action.pipelineIds.length > 0 && action.pipelineIds.every((id) => next.has(id));
        for (const id of action.pipelineIds) {
          if (allSelected) next.delete(id);
          else next.add(id);
        }
        break;
      }
      case 'toggle_one': {
        if (next.has(action.pipelineId)) next.delete(action.pipelineId);
        else next.add(action.pipelineId);
        break;
      }
      case 'reset': {
        set(baseSelectionAtom, new Set());
        return;
      }
    }

    set(baseSelectionAtom, next);
  },
);

export function Export() {
  const [css] = useStyletron();
  const [open, setOpen] = useState(true);
  const navigate = useNavigate();
  const { instanceName } = useParams();
  const [selectedPipelineIds, dispatchSelectedPipelineIds] = useAtom(pipelineSelectionAtom);
  const [searchQuery, setSearchQuery] = useState('');

  const PAGE_SIZE = 20;

  const pipelinesApi = useBifrostApi(PipelinesApi);
  const instanceApi = useBifrostApi(InstancesApi);

  const { data: instance } = useQuery({
    enabled: !!instanceName,
    queryKey: ['instance-details', instanceName],
    queryFn: async () => {
      return instanceApi.readInstances({}).then((res) => res.data.find((i) => i.name === instanceName)!);
    },
  });

  const { data, isLoading, fetchNextPage, isFetchingNextPage, hasNextPage } = useInfiniteQuery({
    enabled: !!instance?.id,
    queryKey: ['instance-pipelines', instance?.id],
    initialPageParam: { current_page: 1, page_size: PAGE_SIZE },
    queryFn: async ({ pageParam }) =>
      pipelinesApi.getFilteredPipelinesList({
        pipelineFilteredListRequestBody: {
          instance_id: instance!.id,
          ...pageParam,
        },
      }),
    getNextPageParam: (prev) => {
      const currentPage = prev.data.pagination.current_page ?? 1;
      const totalPages = prev.data.pagination.total_pages ?? 1;
      return currentPage < totalPages
        ? {
            current_page: currentPage + 1,
            page_size: prev.data.pagination.page_size ?? PAGE_SIZE,
          }
        : undefined;
    },
  });

  const { mutateAsync, isIdle } = useMutation({
    mutationKey: ['export-pipelines'],
    mutationFn: async (pipelineIds: string[]) => {
      if (!instance) return undefined;
      const [response] = await Promise.all([
        await pipelinesApi.bulkExportPipelines({
          bulkExportRequest: {
            instance_id: instance.id,
            pipeline_ids: pipelineIds,
          },
        }),
        new Promise((resolve) => setTimeout(resolve, 300)),
      ]);
      return response;
    },
  });

  const pipelines = data ? data.pages.flatMap((e) => e.data.data) : undefined;

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return pipelines;

    return (pipelines ?? []).filter((p) => p.name.toLowerCase().includes(q));
  }, [pipelines, searchQuery]);

  const allVisibleSelected = filtered
    ? filtered.length > 0 && filtered.every((p) => selectedPipelineIds.has(p.id))
    : false;
  const someVisibleSelected = filtered
    ? filtered.length > 0 && filtered.some((p) => selectedPipelineIds.has(p.id)) && !allVisibleSelected
    : false;

  const sectionStyles = css({
    display: 'flex',
    flexDirection: 'column',
    gap: Tokens.ref.sizing.scale400,
    padding: Tokens.ref.sizing.scale600,
    borderBottom: `1px solid ${Tokens.sys.border.borderOpaque}`,
  });

  return (
    <Modal
      isOpen={open}
      onClose={() => navigate('..')}
      overrides={{
        Close: {
          component: () => null,
        },
        Dialog: {
          style: {
            height: '900px',
            width: '800px',
            display: 'grid',
            gridTemplateRows: 'auto minmax(0, 1fr) auto',
          },
        },
      }}
    >
      <div className={sectionStyles}>
        <HeadingSmall marginBottom="0">Export Pipelines</HeadingSmall>
        <ParagraphSmall marginBottom="0">
          Select source objects to load pipelines, then either download them, choose a target instance to import to, or
          do both.
        </ParagraphSmall>
      </div>
      <div
        className={css({
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: Tokens.ref.sizing.scale600,
          paddingBlockStart: Tokens.ref.sizing.scale600,
        })}
      >
        <div
          className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: Tokens.ref.sizing.scale100,
            marginInline: Tokens.ref.sizing.scale600,
            padding: Tokens.ref.sizing.scale500,
            borderRadius: Tokens.ref.border.radiusSmall,
            border: `1px solid ${Tokens.sys.border.borderOpaque}`,
          })}
        >
          <LabelMedium marginBottom="0">Source Instance</LabelMedium>
          <ParagraphSmall marginBottom="0">{instanceName}</ParagraphSmall>
        </div>
        <div
          className={css({
            display: 'flex',
            flex: '1 1 auto',
            minHeight: 0,
            flexDirection: 'column',
            gap: Tokens.ref.sizing.scale400,
            marginInline: Tokens.ref.sizing.scale600,
            marginBlockEnd: 0,
            padding: Tokens.ref.sizing.scale500,
            borderTopLeftRadius: Tokens.ref.border.radiusSmall,
            borderTopRightRadius: Tokens.ref.border.radiusSmall,
            border: `1px solid ${Tokens.sys.border.borderOpaque}`,
            borderBlockEnd: 0,
          })}
        >
          <div
            className={css({
              display: 'flex',
              alignItems: 'center',
              gap: Tokens.ref.sizing.scale400,
            })}
          >
            <Input
              placeholder="Search pipelines to import..."
              startEnhancer={() => <SearchIcon />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              clearable
            />
          </div>
          <Show
            when={pipelines && !isLoading ? filtered : undefined}
            fallback={
              <div
                className={css({
                  flex: '1 1 auto',
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: Tokens.ref.sizing.scale200,
                  overflowY: 'auto',
                })}
              >
                <Skeleton width="100%" height="36px" />
                <Skeleton width="100%" height="36px" />
                <Skeleton width="100%" height="36px" />
                <Skeleton width="100%" height="36px" />
                <Skeleton width="100%" height="36px" />
              </div>
            }
          >
            {(filtered) => {
              return (
                <>
                  <div
                    className={css({
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      paddingInline: Tokens.ref.sizing.scale400,
                      paddingBlockEnd: Tokens.ref.sizing.scale200,
                      borderBlockEnd: `1px solid ${Tokens.sys.border.borderOpaque}`,
                    })}
                  >
                    <Checkbox
                      checked={allVisibleSelected}
                      isIndeterminate={someVisibleSelected}
                      onChange={() =>
                        dispatchSelectedPipelineIds({
                          type: 'toggle_all',
                          pipelineIds: filtered.map((p) => p.id),
                        })
                      }
                    >
                      Select all
                    </Checkbox>
                    <ParagraphSmall marginBottom="0">
                      {selectedPipelineIds.size}/{pipelines?.length} pipelines selected
                    </ParagraphSmall>
                  </div>
                  <List
                    render={(props) => (
                      <div
                        {...props}
                        className={css({
                          flex: '1 1 auto',
                          minHeight: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: Tokens.ref.sizing.scale200,
                          overflowY: 'auto',
                        })}
                      />
                    )}
                  >
                    {filtered.map((pipeline) => (
                      <div
                        key={pipeline.id}
                        className={css({
                          display: 'flex',
                          alignItems: 'center',
                          borderRadius: Tokens.ref.border.radiusSmall,
                          gap: Tokens.ref.sizing.scale400,
                          border: `1px solid ${Tokens.sys.border.borderOpaque}`,
                          padding: Tokens.ref.sizing.scale400,
                        })}
                      >
                        <Checkbox
                          name={pipeline.id}
                          checked={selectedPipelineIds.has(pipeline.id)}
                          onChange={() => dispatchSelectedPipelineIds({ type: 'toggle_one', pipelineId: pipeline.id })}
                        />
                        {pipeline.name}
                      </div>
                    ))}
                    <List.More onLoadMore={() => hasNextPage && fetchNextPage()} loading={isFetchingNextPage}>
                      <ListLoadingBox itemsCount={3} itemHeight="36px" />
                    </List.More>
                  </List>
                </>
              );
            }}
          </Show>
        </div>
      </div>
      <div
        className={css({
          display: 'flex',
          padding: Tokens.ref.sizing.scale600,
          borderTop: `1px solid ${Tokens.sys.border.borderOpaque}`,
        })}
      >
        <Button kind="secondary" disabled={!isIdle} onClick={() => navigate('..')}>
          Cancel
        </Button>
        <div className={css({ marginLeft: 'auto' })}>
          <Show
            when={isIdle}
            fallback={
              <Button startEnhancer={<ProgressBarCircular size="xsmall" />} disabled>
                Exporting
              </Button>
            }
          >
            <Button
              kind="primary"
              disabled={selectedPipelineIds.size === 0}
              onClick={async () => {
                await mutateAsync(Array.from(selectedPipelineIds))
                  .then((res) => {
                    if (!res) return;
                    const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
                    const timestamp = new Date().toISOString().replace(/:/g, '-');
                    const filename = `pipelines_export_${instanceName || 'instance'}_${timestamp}.json`;
                    downloadBlobFile(blob, filename);
                    setOpen(false);
                  })
                  .catch((e) => {
                    console.error('Failed to export pipelines', e);
                  });
              }}
            >
              Export
            </Button>
          </Show>
        </div>
      </div>
    </Modal>
  );
}
